import { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlus, FaSearch, FaUser } from "react-icons/fa";
import Paging from "../Paging";
import ChildUserEditor from "./ChildUserEditor";
import ChildUserPermissionEditor from "./ChildUserPermissionEditor";
import axiosInstance from "../../axiosInstance";
import * as general from "../../libs/general";
import "../../css/DepartmentModule.css";
import "../../css/EnterprisePermissionPage.css";
import "../../css/ChildUsersPage.css";

const CustomerChildUsersPanel = ({ parentUserId, embedded = false }) => {
  const config = general.getLocalStorage("user_config");
  const adminParentId =
    parentUserId != null && Number(parentUserId) > 0 ? Number(parentUserId) : null;
  const isAdminContext = adminParentId != null;
  const canManage = isAdminContext || config?.canManageChildUsers === true;

  const parentQuery = useMemo(
    () => (adminParentId != null ? { parentUserId: adminParentId } : {}),
    [adminParentId]
  );

  const [pageData, setPageData] = useState({ page: { content: [] } });
  const [keyword, setKeyword] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [accessChecked, setAccessChecked] = useState(false);

  const searchUsers = useCallback(
    async (filter = {}) => {
      setLoading(true);
      setMessage("");
      const page = filter.page !== undefined ? filter.page : 0;
      const size = filter.size !== undefined ? filter.size : itemsPerPage;
      const kw = filter.keyword !== undefined ? filter.keyword : keyword;
      if (filter.keyword !== undefined) setKeyword(filter.keyword);
      if (filter.size !== undefined) setItemsPerPage(filter.size);

      try {
        const response = await axiosInstance.get("/api/users/searchChildUsers", {
          params: {
            page,
            size,
            ...parentQuery,
            ...(kw ? { keyword: kw } : {}),
          },
        });
        setPageData(response.data || { page: { content: [] } });
      } catch (err) {
        setPageData({ page: { content: [] } });
        setMessage(err.message || "Không tải được danh sách user.");
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage, keyword, parentQuery]
  );

  useEffect(() => {
    if (!canManage) {
      setAccessChecked(true);
      return;
    }
    searchUsers({ page: 0 }).finally(() => setAccessChecked(true));
  }, [canManage, searchUsers]);

  const rows = pageData.page?.content || [];

  useEffect(() => {
    const content = pageData.page?.content;
    if (!content?.length) {
      setSelectedUserId(null);
      return;
    }
    setSelectedUserId((prev) => {
      if (prev != null && content.some((row) => row.id === prev)) return prev;
      return content[0].id;
    });
  }, [pageData]);

  const selectedUser = useMemo(
    () => rows.find((row) => row.id === selectedUserId) || null,
    [rows, selectedUserId]
  );

  const handleOpenCreate = () => {
    setEditingUser(null);
    setShowEditor(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingUser(null);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchUsers({ page: 0, keyword });
  };

  const userRowClass = (userId) =>
    selectedUserId === userId
      ? "enterprise-tree__row enterprise-tree__row--strong"
      : "enterprise-tree__row";

  if (!accessChecked) {
    return <div className="py-3 min-h-25" aria-busy="true" />;
  }

  if (!canManage) {
    return (
      <div className="container py-3">
        <div className="alert alert-warning mb-0">Truy cập không được phép.</div>
      </div>
    );
  }

  const rootClass = embedded
    ? "child-users-page child-users-page--embedded department-module"
    : "child-users-page department-module";

  return (
    <div className={rootClass}>
      <div className={embedded ? "container-fluid p-2" : "container-fluid p-2"}>
        <div className="row g-3 align-items-start">
          <div className="col-lg-3">
            <div className="card h-100 d-flex flex-column">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Danh sách users</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                  onClick={handleOpenCreate}
                >
                  <FaPlus size={12} />
                  Thêm user
                </button>
              </div>
              <form className="enterprise-tree-search px-2 py-2 border-bottom bg-light" onSubmit={handleSearchSubmit}>
                <div className="input-group input-group-sm">
                  <input
                    type="search"
                    className="form-control border-end-0"
                    placeholder="Tìm theo tên, email, tên đăng nhập..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    aria-label="Tìm kiếm user"
                  />
                  <button type="submit" className="input-group-text py-0 bg-white border-start-0 btn btn-sm">
                    <FaSearch size={12} className="text-muted" aria-hidden />
                  </button>
                </div>
              </form>
              <div className="card-body p-2 enterprise-tree child-users-list flex-grow-1 min-h-0">
                {loading && <div className="text-muted small px-1">Đang tải…</div>}
                {!loading && message && (
                  <div className="text-danger small px-1">{message}</div>
                )}
                {!loading && !message && rows.length === 0 && (
                  <div className="text-muted small px-1">Chưa có user nào</div>
                )}
                {!loading &&
                  rows.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={userRowClass(item.id)}
                      onClick={() => setSelectedUserId(item.id)}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        handleOpenEdit(item);
                      }}
                    >
                      <span className="enterprise-tree__row-main">
                        <FaUser className="enterprise-tree__icon-dept" aria-hidden />
                        <span className="enterprise-tree__label text-start">
                          <span className="d-block text-truncate">
                            {item.name || item.username || item.email || "—"}
                          </span>
                          {(item.username || item.email) && (
                            <span className="child-users-list__meta">
                              {[item.username, item.email].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  ))}
              </div>
              <div className="child-users-paging px-2 py-2">
                <Paging
                  pageData={pageData}
                  totalLabel="Tổng"
                  hidePaginationWhenSinglePage
                  onPageChange={(p) => searchUsers({ page: p - 1 })}
                  onItemsPerPageChange={(e) =>
                    searchUsers({ page: 0, size: parseInt(e.target.value, 10) })
                  }
                />
              </div>
            </div>
          </div>

          <div className="col-lg-9 d-flex flex-column min-h-0">
            <div className="flex-grow-1 min-h-0 d-flex flex-column">
              <ChildUserPermissionEditor
                variant="panel"
                user={selectedUser}
                parentUserId={adminParentId}
                onSaved={() => searchUsers()}
              />
            </div>
          </div>
        </div>
      </div>

      {showEditor && (
        <ChildUserEditor
          show={showEditor}
          model={editingUser}
          parentUserId={adminParentId}
          onClose={handleCloseEditor}
          onSaved={() => searchUsers()}
        />
      )}
    </div>
  );
};

export default CustomerChildUsersPanel;
